import React, { useEffect, useState } from "react";
import API_URL from "../../apiConfig";

function formatMoney(amount, currency = "CAD") {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "-";

  return new Intl.NumberFormat("es-CA", {
    style: "currency",
    currency: String(currency || "CAD").toUpperCase(),
  }).format(value / 100);
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("es-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function statusLabel(status) {
  const value = String(status || "").toLowerCase();
  if (value === "succeeded") return "Pagada";
  if (value === "refunded") return "Reembolsada";
  if (value === "pending") return "Pendiente";
  if (value === "failed") return "Fallida";
  return status || "-";
}

function statusStyle(status) {
  const value = String(status || "").toLowerCase();

  if (value === "succeeded") {
    return {
      ...badgeStyle,
      background: "#eefaf3",
      color: "#166534",
      border: "1px solid #bbf7d0",
    };
  }

  if (value === "refunded") {
    return {
      ...badgeStyle,
      background: "#f8fafc",
      color: "#475569",
      border: "1px solid #cbd5e1",
    };
  }

  if (value === "failed") {
    return {
      ...badgeStyle,
      background: "#fef2f2",
      color: "#b42318",
      border: "1px solid #fecaca",
    };
  }

  return {
    ...badgeStyle,
    background: "#fff7ed",
    color: "#9a3412",
    border: "1px solid #fed7aa",
  };
}

function accessLabel(purchase) {
  if (purchase?.access_eligible === true) return "Elegible";
  if (String(purchase?.status || "").toLowerCase() === "refunded") return "Revocado por reembolso";
  return "No elegible";
}

export default function MemberPurchasesPanel({ memberId }) {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!memberId) {
      setPurchases([]);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    async function loadPurchases() {
      try {
        setLoading(true);
        setError("");

        const session = JSON.parse(localStorage.getItem("zedro_session") || "null");
        const response = await fetch(
          `${API_URL}/api/admin/purchases/members/${memberId}`,
          {
            headers: {
              Authorization: `Bearer ${session?.token || ""}`,
            },
            signal: controller.signal,
          }
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok || !data?.ok) {
          throw new Error(data?.error || "cannot_load_member_purchases");
        }

        setPurchases(Array.isArray(data.purchases) ? data.purchases : []);
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.error("[GAU_MEMBER_PURCHASES_LOAD_ERROR]", err);
        setPurchases([]);
        setError("No fue posible cargar el historial de compras.");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadPurchases();

    return () => controller.abort();
  }, [memberId]);

  return (
    <section style={sectionStyle} aria-labelledby="gau-purchases-title">
      <div style={headerStyle}>
        <div>
          <div style={eyebrowStyle}>Historial comercial</div>
          <h3 id="gau-purchases-title" style={titleStyle}>
            Compras independientes
          </h3>
          <p style={descriptionStyle}>
            Compras de pago único registradas por producto y precio. Se muestran separadas de las membresías para conservar la trazabilidad comercial correcta.
          </p>
        </div>

        <span style={countBadgeStyle}>
          {loading ? "Cargando" : `${purchases.length} compra${purchases.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {loading ? (
        <p style={stateTextStyle}>Cargando historial comercial...</p>
      ) : error ? (
        <p style={errorTextStyle}>{error}</p>
      ) : purchases.length === 0 ? (
        <p style={stateTextStyle}>No hay compras independientes registradas para este usuario.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Producto</th>
                <th style={thStyle}>Tipo</th>
                <th style={thStyle}>Monto</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}>Acceso</th>
                <th style={thStyle}>Fecha</th>
                <th style={thStyle}>Referencia Stripe</th>
              </tr>
            </thead>

            <tbody>
              {purchases.map((purchase) => (
                <tr key={purchase.id}>
                  <td style={tdStyle}>
                    <strong style={productNameStyle}>{purchase.product_name || "-"}</strong>
                    {purchase.price_label ? (
                      <div style={secondaryTextStyle}>{purchase.price_label}</div>
                    ) : null}
                  </td>
                  <td style={tdStyle}>
                    <span style={typeBadgeStyle}>{purchase.product_type || "-"}</span>
                    <div style={secondaryTextStyle}>{purchase.price_type || "-"}</div>
                  </td>
                  <td style={tdStyle}>{formatMoney(purchase.amount, purchase.currency)}</td>
                  <td style={tdStyle}>
                    <span style={statusStyle(purchase.status)}>{statusLabel(purchase.status)}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={purchase.access_eligible ? accessGrantedStyle : accessInactiveStyle}>
                      {accessLabel(purchase)}
                    </span>
                  </td>
                  <td style={tdStyle}>{formatDate(purchase.created_at)}</td>
                  <td style={tdStyle}>
                    <code style={stripeReferenceStyle}>
                      {purchase.stripe_payment_intent_id || "-"}
                    </code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const sectionStyle = {
  maxWidth: 960,
  margin: "24px auto 40px",
  padding: "20px 22px",
  borderRadius: 18,
  background: "#ffffff",
  border: "1px solid #e6ebf1",
  boxShadow: "0 14px 32px rgba(15, 47, 70, 0.045)",
  boxSizing: "border-box",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  paddingBottom: 16,
  borderBottom: "1px solid #eef2f7",
};

const eyebrowStyle = {
  marginBottom: 5,
  color: "#CCA352",
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const titleStyle = {
  margin: 0,
  color: "#264763",
  fontSize: 20,
  fontWeight: 900,
  letterSpacing: "-0.025em",
};

const descriptionStyle = {
  maxWidth: 720,
  margin: "6px 0 0",
  color: "#64748b",
  fontSize: 13,
  lineHeight: 1.55,
};

const countBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 84,
  padding: "6px 10px",
  borderRadius: 999,
  background: "#f8fafc",
  border: "1px solid #dbe4ef",
  color: "#264763",
  fontSize: 11,
  fontWeight: 900,
  whiteSpace: "nowrap",
};

const stateTextStyle = {
  margin: "16px 0 0",
  color: "#66758a",
  fontSize: 14,
};

const errorTextStyle = {
  ...stateTextStyle,
  color: "#b42318",
};

const tableStyle = {
  width: "100%",
  minWidth: 900,
  borderCollapse: "collapse",
  marginTop: 10,
};

const thStyle = {
  textAlign: "left",
  padding: "10px",
  borderBottom: "1px solid #e6ebf1",
  color: "#64748b",
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.035em",
};

const tdStyle = {
  padding: "11px 10px",
  borderBottom: "1px solid #f0f3f6",
  color: "#2c3b51",
  fontSize: 12,
  verticalAlign: "top",
};

const productNameStyle = {
  color: "#264763",
  fontSize: 13,
};

const secondaryTextStyle = {
  marginTop: 4,
  color: "#7b8797",
  fontSize: 10,
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "4px 8px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 900,
  whiteSpace: "nowrap",
};

const typeBadgeStyle = {
  ...badgeStyle,
  background: "#f8fafc",
  border: "1px solid #dbe4ef",
  color: "#264763",
};

const accessGrantedStyle = {
  ...badgeStyle,
  background: "#eefaf3",
  border: "1px solid #bbf7d0",
  color: "#166534",
};

const accessInactiveStyle = {
  ...badgeStyle,
  background: "#f8fafc",
  border: "1px solid #cbd5e1",
  color: "#475569",
};

const stripeReferenceStyle = {
  color: "#264763",
  fontSize: 10,
  fontWeight: 700,
  wordBreak: "break-all",
};
